import _get from 'lodash/get';
import { TFunction } from 'i18next';
import { AiDocumentationFieldEnum } from 'graphql-common';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import Select from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import ChipValueContainer from '@lib/components/Select/components/ChipValueContainer';
import ChipOption from '@lib/components/Select/components/ChipOption';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { getOptionValidation } from '@lib/utils/validationUtils';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import { CategoryOption } from '../../utils/resolveCategoryPermissionOverride';
import getDocumentPermissionFields from '../../utils/getDocumentPermissionFields';
import AiFieldAssist from '../components/AiFieldAssist';
import UseCategoryDefaultsAction from '../components/UseCategoryDefaultsAction';

const getCategoryOption = (values: Values) =>
  _get(values, DocumentFormFieldNames.Category, null) as CategoryOption | null;

// "Custom" means at least one checkbox no longer matches the selected
// category's default — with no category selected there's nothing to diverge
// from.
const getIsCustomPermissions = (values: Values) => {
  const category = getCategoryOption(values);
  if (!category) return false;
  return (
    !!_get(values, DocumentFormFieldNames.AllowDownload) !==
      !!category.allowDownload ||
    !!_get(values, DocumentFormFieldNames.AllowPrint) !== !!category.allowPrint
  );
};

export default function getCategoryFields({
  t,
  categoriesLoadOptions,
  showTitle = false,
  isFullAdmin = true,
  isPlanLocked,
  hasCategoriesAccess = true,
  openAddCategoryModal,
  categoryCacheKey,
  isAiAssistVisible,
  isEdit,
  documentationVersionId,
  aiCredits,
  detectionUiState,
  onDismiss,
  withPermissionsFields = false,
}: {
  t: TFunction<'translation', undefined>;
  categoriesLoadOptions: GetLoadOptions;
  showTitle?: boolean;
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  hasCategoriesAccess?: boolean;
  openAddCategoryModal?: () => void;
  categoryCacheKey?: number;
  // AI auto-fill — same contract as the other AiFieldAssist-backed fields in
  // getDocumentFormFields.tsx; omitted (create-flow default) hides the
  // affordance entirely.
  isAiAssistVisible?: (values: Record<string, unknown>) => boolean;
  isEdit?: boolean;
  documentationVersionId?: string;
  aiCredits?: AiCredits | null;
  detectionUiState?: AiFieldUiState;
  onDismiss?: () => void;
  // "Allow download"/"Allow printing" checkboxes, prefilled from the
  // selected category, with a "custom permissions" reset action. Opt-in
  // since not every category field consumer wants them (e.g. the quick
  // AI-import row).
  withPermissionsFields?: boolean;
}): FieldItem[] {
  if (!hasCategoriesAccess) return [];
  // Locked field is a Full-Admin-only upgrade affordance; hide for User role.
  if (isPlanLocked && !isFullAdmin) return [];
  return [
    ...(showTitle
      ? [
          {
            name: 'categoryTitle',
            customRender: () => (
              <FieldTitleRender title={t('category')} key="categoryTitle" />
            ),
          } as FieldItem,
        ]
      : []),
    {
      name: DocumentFormFieldNames.Category,
      label: t('category'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: categoriesLoadOptions,
        defaultOptions: false,
        isClearable: true,
        maxMenuHeight: 200,
        usePortal: true,
        cacheUniqs: [categoryCacheKey],
        createNewOptionProps:
          openAddCategoryModal && !isPlanLocked
            ? {
                title: t('create-new-category'),
                onCLick: openAddCategoryModal,
              }
            : undefined,
        components: {
          ValueContainer: ChipValueContainer,
          Option: ChipOption,
        },
        disabled: isPlanLocked,
        disabledTooltipProps: isPlanLocked
          ? { body: t('unlock-categories-tooltip') }
          : undefined,
      },
      getComponentProps: (values) => {
        if (!isAiAssistVisible?.(values)) return undefined;
        // The clear indicator only renders with a value — slot 3 (adornment +
        // dropdown + clear) when a category is selected, else slot 2.
        return {
          rightAdornmentSlot: _get(values, DocumentFormFieldNames.Category)
            ? 3
            : 2,
          rightAdornment: (
            <AiFieldAssist
              field={AiDocumentationFieldEnum.Category}
              targetFieldName={DocumentFormFieldNames.Category}
              isEdit={isEdit}
              documentationVersionId={documentationVersionId}
              creditsRemaining={aiCredits}
              detectionUiState={detectionUiState}
              detectingLabel={t('ai-detecting-code')}
              onDismiss={onDismiss}
              labels={{
                idleLabel: t('ai-suggest-with-ai'),
                idleCta: t('ai-suggest-category-cta'),
                regenerateCta: t('ai-regenerate'),
              }}
            />
          ),
        };
      },
      fieldChangeCallBack: ({
        setValue,
        name,
        values,
      }: FieldChangeCallBackArgs) => {
        if (name === DocumentFormFieldNames.Category) {
          setValue(DocumentFormFieldNames.Approver, null, {
            shouldValidate: false,
          });
          if (withPermissionsFields) {
            const category = getCategoryOption(values);
            if (category) {
              setValue(
                DocumentFormFieldNames.AllowDownload,
                !!category.allowDownload,
                { shouldValidate: false },
              );
              setValue(
                DocumentFormFieldNames.AllowPrint,
                !!category.allowPrint,
                { shouldValidate: false },
              );
            }
          }
        }
      },
    },
    ...(withPermissionsFields
      ? getDocumentPermissionFields({
          t,
          isPlanLocked,
          names: {
            titleName: DocumentFormFieldNames.DocumentPermissionsTitle,
            allowDownloadName: DocumentFormFieldNames.AllowDownload,
            allowPrintName: DocumentFormFieldNames.AllowPrint,
          },
          // The block's closing field carries the "custom permissions" notice
          // for the whole block, so it watches both checkboxes.
          allowPrintFieldProps: {
            isUseWatch: true,
            message: {
              getTextFn: (values: Values) =>
                getIsCustomPermissions(values)
                  ? t('custom-permissions-applied-to-document')
                  : undefined,
              getActions: (values: Values) => {
                const category = getCategoryOption(values);
                if (!category || !getIsCustomPermissions(values)) {
                  return undefined;
                }
                return (
                  <UseCategoryDefaultsAction
                    allowDownload={!!category.allowDownload}
                    allowPrint={!!category.allowPrint}
                  />
                );
              },
            },
          },
        })
      : []),
  ];
}
